import { Button, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IoIosRefresh } from 'react-icons/io';

import { PinPad } from '../../../../components/formComponents';
import { useUpdateVerificationSessionMutation } from '../../../../gql/generated/graphql';

const OTP_LENGTH = 6;
const OTP_EXPIRATION_MINS = 10;
const OTP_EXPIRATION_SECONDS = OTP_EXPIRATION_MINS * 60;
const RESEND_COOLDOWN_SECONDS = 30;
const MAX_RESENDS_PER_SESSION = 3;
const MAX_VERIFICATION_ATTEMPTS = 3;

type VerifyCodePanelProps = {
  entityId: string;
  verificationSessionId: string;
  onResendCode: () => Promise<string>;
  onEditPhoneNumber: () => void;
  onVerificationSuccess: () => Promise<void>;
  onSkipVerification: () => void;
};

const VerifyCodePanel = ({
  entityId,
  verificationSessionId,
  onResendCode,
  onEditPhoneNumber,
  onVerificationSuccess,
  onSkipVerification,
}: VerifyCodePanelProps) => {
  const [code, setCode] = useState('');
  const [resendCount, setResendCount] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [expirationTime, setExpirationTime] = useState(OTP_EXPIRATION_SECONDS);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const submittedCodeRef = useRef<string | null>(null);

  const [updateVerificationSession] = useUpdateVerificationSessionMutation({
    context: {
      isErrorHandled: true,
    },
  });

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Expiration countdown timer
  useEffect(() => {
    if (isExpired || expirationTime <= 0) return undefined;

    const timer = setInterval(() => {
      setExpirationTime((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          setError('This code has expired.');
          setResendCooldown(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isExpired, expirationTime]);

  const verifyCode = useCallback(
    async (otpCode: string) => {
      if (submittedCodeRef.current === otpCode) return;

      if (isExpired) {
        setError('This code has expired.');
        return;
      }

      submittedCodeRef.current = otpCode;
      setIsVerifying(true);
      setError(null);
      setVerificationAttempts((prev) => prev + 1);

      try {
        const result = await updateVerificationSession({
          variables: {
            entityId,
            verificationSessionId,
            code: otpCode,
          },
        });

        if (result.data?.updateVerificationSession?.status === 'verified') {
          await onVerificationSuccess();
        } else {
          setError("That code isn't correct.");
          setCode('');
          submittedCodeRef.current = null;
        }
      } catch {
        await onResendCode();
        setError(
          verificationAttempts + 1 >= MAX_VERIFICATION_ATTEMPTS
            ? resendCount >= MAX_RESENDS_PER_SESSION
              ? 'Too many attempts.'
              : 'Too many attempts. Please resend a new code.'
            : 'Verification failed. Please try again.',
        );
        setCode('');
        submittedCodeRef.current = null;
      } finally {
        setIsVerifying(false);
      }
    },
    [
      entityId,
      verificationSessionId,
      verificationAttempts,
      isExpired,
      updateVerificationSession,
      onVerificationSuccess,
    ],
  );

  useEffect(() => {
    if (code.length === OTP_LENGTH && !isVerifying && !isExpired) {
      verifyCode(code);
    }
  }, [code, isVerifying, isExpired, verifyCode]);

  const handleResendCode = async () => {
    if (resendCooldown > 0 || resendCount >= MAX_RESENDS_PER_SESSION) return;

    try {
      await onResendCode();
      setResendCount((prev) => prev + 1);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setExpirationTime(OTP_EXPIRATION_SECONDS);
      setIsExpired(false);
      setVerificationAttempts(0);
      setError(null);
      setCode('');
      submittedCodeRef.current = null;
    } catch {
      setError('Failed to resend code. Please try again.');
    }
  };

  const canResend = resendCooldown === 0 && resendCount < MAX_RESENDS_PER_SESSION;
  const remainingAttempts = MAX_VERIFICATION_ATTEMPTS - verificationAttempts;
  const isInputDisabled =
    isVerifying || isExpired || verificationAttempts >= MAX_VERIFICATION_ATTEMPTS;
  const showSkipLink =
    verificationAttempts >= MAX_VERIFICATION_ATTEMPTS || resendCount >= MAX_RESENDS_PER_SESSION;

  return (
    <VStack spacing={4}>
      {!isExpired && (
        <Text textAlign="center">
          Enter the one-time code to continue. The code is valid for {OTP_EXPIRATION_MINS} minutes.
        </Text>
      )}

      <PinPad
        value={code}
        onChange={setCode}
        length={OTP_LENGTH}
        isDisabled={isInputDisabled}
        autoFocus
      />

      {error && (
        <Text fontSize="sm" color="red.500" textAlign="center">
          {error}
          {!isExpired &&
            remainingAttempts > 0 &&
            remainingAttempts < MAX_VERIFICATION_ATTEMPTS &&
            ` (${remainingAttempts} attempt${remainingAttempts === 1 ? '' : 's'} remaining)`}
        </Text>
      )}

      {isVerifying && (
        <Text fontSize="sm" color="gray.500" textAlign="center">
          Verifying...
        </Text>
      )}

      {resendCount < MAX_RESENDS_PER_SESSION && (
        <Button
          onClick={handleResendCode}
          isDisabled={!canResend}
          variant="outline"
          size="sm"
          leftIcon={<IoIosRefresh />}
          mt={4}
        >
          {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
        </Button>
      )}

      <VStack spacing={2} mt={4}>
        <Button onClick={onEditPhoneNumber} variant="link" textAlign="center">
          Edit Phone Number
        </Button>
        {showSkipLink && (
          <Button onClick={onSkipVerification} variant="link" textAlign="center">
            Skip Phone Verification
          </Button>
        )}
      </VStack>
    </VStack>
  );
};

export default VerifyCodePanel;
